/**
 * Browser-side bridge between sandboxed Page iframes and the host app. It
 * handles two categories of message the iframe posts to `window.parent`:
 *
 *   A. `rpc` — an async request/reply couriered to the `/api/v2/agentPage/rpc`
 *      endpoint (tool calls: readCRM, crmMutation, …). The bridge is a thin
 *      postMessage ↔ HTTP courier here; ALL security for this path lives
 *      server-side (see "Security boundary" below).
 *   B. `page.navigate` / `page.openEntity` — fire-and-forget HOST actions the
 *      sandboxed iframe can't perform itself (it can't navigate the top frame
 *      or open popups). The bridge acts on these entirely client-side in the
 *      trusted parent window: navigate the host to an in-app hash route, or
 *      open/create a CRM record via `pageEntityActions`. No HTTP, no reply. The
 *      trust decisions for this path (same-origin hash only, entity allowlist,
 *      per-entity prefill allowlist) DO live here — that's the whole point,
 *      since the parent is the trusted surface the iframe is escalating to.
 *
 * Lifecycle:
 *   1. PageIframe mounts → `sandboxBridge.register(iframeWindow, slug)`.
 *   2. The iframe's SDK posts a message (see A/B) to `window.parent`. Bridge
 *      listens at the parent's `window` level.
 *   3. Bridge validates the message came from a known iframe (via
 *      `event.source`), then couriers (A) or acts (B).
 *   4. For `rpc`, the API response routes back to the iframe as
 *      `{type:'rpc-response', id, data}` or `{type:'rpc-error', id, error}`.
 *   5. PageIframe unmounts → `sandboxBridge.unregister(iframeWindow)`.
 *
 * Why per-window registration:
 *   - `event.origin` is `"null"` for sandboxed iframes — we can't validate by
 *     origin string, so we validate by Window identity (which the browser
 *     controls and scripts can't forge).
 *   - Multiple `<PageIframe>`s can exist on the same parent page; each
 *     registers its own slug.
 *
 * Security boundary for the `rpc` path lives in the API, not here:
 *   - Tool allowlist (`availableTo.includes('page')`)
 *   - Rate limit
 *   - User scope / permissions
 *   - Approval gates (when WEB-6730 lands)
 * Don't bake `rpc` security decisions in here — they belong server-side where
 * they share infrastructure with the agent runtime.
 */

import { isPageEntity, type PageEntity } from 'App/sandboxed-page/pageEntities';

interface RegisteredIframe {
	win: Window;
	slug: string;
}

interface RpcRequest {
	type: 'rpc';
	id: number;
	tool: string;
	args?: unknown;
}

/** Fire-and-forget navigation request — handled client-side, never hits the API. */
interface NavigateRequest {
	type: 'page.navigate';
	url: string;
}

/**
 * Fire-and-forget request to open — or create — a CRM record in the host.
 * `payload` is opaque here: the entity's own prefill allowlist is applied in
 * `pageEntityActions`, which is also where the modal/drawer services live.
 */
interface OpenEntityRequest {
	type: 'page.openEntity';
	entity: PageEntity;
	payload?: unknown;
}

const RPC_ENDPOINT = '/api/v2/agentPage/rpc';

function isRpcRequest(data: unknown): data is RpcRequest {
	if (!data || typeof data !== 'object') return false;
	const d = data as Partial<RpcRequest>;
	return d.type === 'rpc' && typeof d.id === 'number' && typeof d.tool === 'string';
}

function isNavigateRequest(data: unknown): data is NavigateRequest {
	if (!data || typeof data !== 'object') return false;
	const d = data as Partial<NavigateRequest>;
	return d.type === 'page.navigate' && typeof d.url === 'string';
}

function isOpenEntityRequest(data: unknown): data is OpenEntityRequest {
	if (!data || typeof data !== 'object') return false;
	const d = data as Partial<OpenEntityRequest>;
	// The entity must be one this app knows how to open; the payload's own
	// validation happens per entity, one layer down.
	return d.type === 'page.openEntity' && isPageEntity(d.entity);
}

export class SandboxBridge {
	private iframes = new Map<Window, RegisteredIframe>();
	private listenerAttached = false;

	register(win: Window | null, slug: string): void {
		if (!win || !slug) return;
		this.iframes.set(win, { win, slug });
		this.ensureListener();
	}

	unregister(win: Window | null): void {
		if (!win) return;
		this.iframes.delete(win);
		// We deliberately leave the listener attached. The cost is one no-op
		// branch per parent-window message; reattaching on every PageIframe
		// mount/unmount cycle would risk subscribe/unsubscribe ordering bugs.
	}

	private ensureListener(): void {
		if (this.listenerAttached) return;
		if (typeof window === 'undefined') return; // server-side rendering safety
		window.addEventListener('message', this.onMessage);
		this.listenerAttached = true;
	}

	private onMessage = async (event: MessageEvent): Promise<void> => {
		const source = event.source as Window | null;
		if (!source) return;
		const reg = this.iframes.get(source);
		if (!reg) return; // message from an unregistered/unknown source — ignore silently

		const data = event.data as unknown;

		// Navigation runs entirely in the host (this window is the trusted,
		// un-sandboxed top frame): the iframe can't navigate the top frame or
		// open popups, so it asks us to. No API round-trip, no reply.
		if (isNavigateRequest(data)) {
			this.navigate(data.url);
			return;
		}
		if (isOpenEntityRequest(data)) {
			this.openEntity(data.entity, data.payload);
			return;
		}

		if (!isRpcRequest(data)) return;

		const { id, tool, args } = data;
		try {
			const body = await this.callApi(reg.slug, tool, args);
			if (body && typeof body.error === 'string') {
				this.reply(source, { type: 'rpc-error', id, error: body.error });
			} else {
				this.reply(source, { type: 'rpc-response', id, data: body?.data });
			}
		} catch (err) {
			const message = err instanceof Error ? err.message : 'Network error';
			this.reply(source, { type: 'rpc-error', id, error: message });
		}
	};

	/**
	 * Exposed as a method so tests can stub network easily without monkey-patching
	 * global fetch. Production path uses `window.fetch`.
	 */
	protected async callApi(
		slug: string,
		tool: string,
		args: unknown
	): Promise<{ data?: unknown; error?: string } | null> {
		const res = await fetch(RPC_ENDPOINT, {
			method: 'POST',
			credentials: 'same-origin',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ slug, tool, args })
		});
		// The endpoint deliberately returns 200 for tool-level errors so this
		// branch only fires on transport/auth failures.
		if (!res.ok && res.status !== 200) {
			return { error: `${res.status} ${res.statusText}` };
		}
		const json = (await res.json().catch(() => null)) as { data?: unknown; error?: string } | null;
		return json;
	}

	/**
	 * Navigate the host app on behalf of a sandboxed Page. A same-origin `#/…`
	 * hash route navigates in place (an in-app deep link — no popup, no sandbox
	 * permission needed).
	 *
	 * ONLY same-origin `#/…` routes are honoured. We deliberately do NOT open
	 * external URLs (no `window.open`): the iframe sandbox blocks popups and
	 * external navigation, and preserving that guarantee here means a Page whose
	 * content is influenced by untrusted CRM data can't make the trusted host
	 * open an arbitrary external site (a redirect/phishing vector). Anything
	 * that isn't a same-origin in-app hash route is ignored.
	 */
	private navigate(rawUrl: string): void {
		let url: URL;
		try {
			url = new URL(rawUrl, window.location.href);
		} catch {
			return; // unparseable — ignore
		}
		if (url.origin === window.location.origin && url.hash.startsWith('#/')) {
			window.location.hash = url.hash;
		}
	}

	/**
	 * Open or create a CRM record on behalf of a sandboxed Page. The payload's
	 * per-entity validation, the id hydration and the modal/drawer routing all
	 * live in `pageEntityActions`; this method's only job is to hand it the
	 * bridge's own `navigate`, so the "same-origin hash routes only" rule stays
	 * in one place.
	 *
	 * `pageEntityActions` is lazily required: it pulls in the modal/drawer
	 * services and the `Tools` global, which aren't available at module-eval
	 * time and would break this bridge's dependency-free unit test. Protected so
	 * tests can stub it, mirroring `callApi`.
	 */
	protected openEntity(entity: PageEntity, payload: unknown): void {
		try {
			// eslint-disable-next-line @typescript-eslint/no-var-requires
			const { openPageEntity } = require('./pageEntityActions');
			// Fire-and-forget by contract, so a rejected promise has to be caught
			// here — an unhandled rejection out of a message handler is invisible.
			Promise.resolve(openPageEntity(entity, payload, (url: string) => this.navigate(url))).catch(
				(err: unknown) => {
					// eslint-disable-next-line no-console
					console.error('[sandboxBridge] openEntity failed', err);
				}
			);
		} catch (err) {
			// Like the rpc path's try/catch, a bridge action must never throw out
			// of the async message handler (unhandled rejection). Surface it for
			// debugging rather than swallowing.
			// eslint-disable-next-line no-console
			console.error('[sandboxBridge] openEntity failed', err);
		}
	}

	private reply(target: Window, msg: object): void {
		try {
			// `'*'` targetOrigin: sandboxed iframes report `event.origin === "null"`,
			// so we can't echo back a specific origin. The iframe-side marshaler
			// in @upsales/pages-sdk validates the message shape (by `id` and
			// `type`) before resolving the promise.
			target.postMessage(msg, '*');
		} catch {
			// iframe gone (unmounted between request and reply) — drop silently
		}
	}
}

// Singleton. PageIframe imports this and calls register/unregister.
export const sandboxBridge = new SandboxBridge();
